import React from "react"
import type { SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router"

import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganization } from "@/contexts"
import { useNotification } from "@/contexts/notification-context"
import type {
  CreateCredentialFormValues,
  CreateCredentialSchema,
} from "@/modules/admin/credentials"
import {
  createCredentialSchema,
  getCredentialSubmitDetail,
  getCredentialSubmitErrorKey,
} from "@/modules/admin/credentials"
import { CreateCredentialFields } from "@/modules/admin/pages/admin-credential-new-route/create-credential-fields"
import { Form } from "@/nzds/forms/form/form"
import { ResetButton } from "@/nzds/forms/reset-button/reset-button"
import { SubmitButton } from "@/nzds/forms/submit-button/submit-button"
import { useForm } from "@/nzds/forms/use-form/use-form"
import type {
  CreateCredentialPayload,
  CreateCredentialResponse,
} from "@/services/credential"
import {
  useCreateCredential,
  useResourceProviders,
  useResourceTypes,
} from "@/services/credential"
import { useUrl } from "@/utils/url"

import { Link as MuiLink } from "@mui/material"
import Stack from "@mui/material/Stack"

const defaultValues: CreateCredentialFormValues = {
  notes: "",
  password: "",
  provider: null,
  resources: [],
  username: "",
  website: "",
}

const buildCreatePayload = (
  values: CreateCredentialSchema,
  resourceType: string,
  provider: { providerName: string } | { resourceProviderName: string }
): CreateCredentialPayload => ({
  notes: values.notes,
  password: values.password,
  resourceType,
  username: values.username,
  website: values.website,
  ...provider,
})

const requireResourceProviderId = (
  result: CreateCredentialResponse
): string => {
  const providerId = result.resourceProviderId
  if (!providerId) {
    throw new Error("Create response missing resourceProviderId")
  }
  return providerId
}

export const AdminCredentialNewRoute: React.FC = () => {
  const { t } = useTranslation("common")
  const navigate = useNavigate()
  const { activeOrganization } = useActiveOrganization()
  const { setNotification } = useNotification()
  const { adminCredentialsPath } = useUrl()
  const [submitErrorMessage, setSubmitErrorMessage] = React.useState<
    string | null
  >(null)

  const organizationId = activeOrganization?.id

  const form = useForm({
    // Empty Autocomplete uses null; schema rejects null until a provider is chosen.
    defaultValues: defaultValues as CreateCredentialSchema,
    // Keep Save eligibility current while the last required field has focus.
    mode: "onTouched",
    schema: createCredentialSchema,
  })

  const { createCredentialIsPending, createCredentialMutateAsync } =
    useCreateCredential()
  const { resourceProvidersData, resourceProvidersIsLoading } =
    useResourceProviders({
      organizationId: organizationId ?? "",
    })
  const { resourceTypesData, resourceTypesIsLoading } = useResourceTypes({
    organizationId: organizationId ?? "",
  })

  const providers = resourceProvidersData ?? []
  const resourceOptions = resourceTypesData ?? []
  const isCatalogLoading = resourceProvidersIsLoading || resourceTypesIsLoading

  const navigateToList = () => {
    void navigate(adminCredentialsPath, { replace: true })
  }

  const handleCancel = () => {
    if (createCredentialIsPending) {
      return
    }

    navigateToList()
  }

  const handleSubmit: SubmitHandler<CreateCredentialSchema> = async (
    values
  ) => {
    if (!organizationId) {
      return
    }

    setSubmitErrorMessage(null)

    try {
      // Pending multi-resource creates must go first-then-rest: Pipelines only
      // returns the new catalog id after the initial resource_provider_name
      // request. Later failures can leave that first credential applied.
      const [firstResource, ...remainingResources] = values.resources
      let providerId =
        values.provider.kind === "existing" ? values.provider.id : undefined

      const firstResult = await createCredentialMutateAsync({
        organizationId,
        payload: buildCreatePayload(
          values,
          firstResource,
          providerId
            ? { providerName: providerId }
            : { resourceProviderName: values.provider.name }
        ),
      })

      providerId ??= requireResourceProviderId(firstResult)

      if (remainingResources.length > 0) {
        const resolvedProviderId = providerId
        await Promise.all(
          remainingResources.map(async (resourceType) =>
            createCredentialMutateAsync({
              organizationId,
              payload: buildCreatePayload(values, resourceType, {
                providerName: resolvedProviderId,
              }),
            })
          )
        )
      }

      navigateToList()
    } catch (error: unknown) {
      const errorKey = getCredentialSubmitErrorKey(error, "create")
      setSubmitErrorMessage(getCredentialSubmitDetail(error) ?? t(errorKey))

      if (errorKey === "admin.credentialsUi.createError") {
        setNotification({
          message: t(errorKey),
          type: "error",
        })
      }
    }
  }

  if (!organizationId) {
    return null
  }

  return (
    <Form<CreateCredentialSchema>
      aria-label={t("admin.credentialsUi.createDialog.title")}
      form={form}
      onSubmit={handleSubmit}
    >
      <Stack sx={{ gap: 2 }}>
        <PageTitle
          breadcrumbActions={
            <MuiLink
              component={Link}
              to={adminCredentialsPath}
              underline="hover"
            >
              {t("admin.credentials")}
            </MuiLink>
          }
          heading={t("admin.credentialsUi.createDialog.title")}
          primaryActions={
            <>
              <ResetButton
                disabled={createCredentialIsPending}
                onClick={handleCancel}
                variant="outlined"
              >
                {t("button.cancel")}
              </ResetButton>
              <SubmitButton
                disabled={createCredentialIsPending || isCatalogLoading}
                loading={createCredentialIsPending}
              >
                {t("button.save")}
              </SubmitButton>
            </>
          }
        />
        <PageCard>
          <CreateCredentialFields
            providers={providers}
            resourceOptions={resourceOptions}
            resourceProvidersIsLoading={resourceProvidersIsLoading}
            resourceTypesIsLoading={resourceTypesIsLoading}
            submitErrorMessage={submitErrorMessage}
          />
        </PageCard>
      </Stack>
    </Form>
  )
}
